import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from "react";
import { Check, X } from "lucide-react";
import alwaysPhoto from "../../assets/home/reception-always.jpg";
import channelsPhoto from "../../assets/home/reception-channels.jpg";
import systemsPhoto from "../../assets/home/reception-systems.jpg";
import bookingPhoto from "../../assets/home/reception-learning.jpg";

/* Tabs drive photo-backed panels. Autoplay pauses on hover, keyboard focus,
   hidden pages and reduced-motion preferences. Pain-point copy stays separate
   from the photographs so it remains readable and accessible. */
type Panel = {
  id: string;
  tab: string;
  title: string;
  label: string;
  checks: ReactNode[];
  pains: string[];
  image: string;
};

const PANELS: Panel[] = [
  {
    id: "always",
    tab: "7×24 接待",
    title: "每一通來電，隨時都有人接。",
    label: "為你帶來",
    checks: [
      <>下班後、週末及假期<strong>照常接待</strong></>,
      <>同時處理多個查詢，<strong>永遠不佔線</strong></>,
      <>客人<strong>無需等待</strong>，即時得到回應</>,
      <>假日及深夜查詢<strong>即時消化</strong>，不再積壓</>,
    ],
    pains: [
      "收工後來電無人接聽",
      "客人等太耐，轉投對手",
      "前台被電話綁住，難顧現場",
      "假日查詢積壓成堆",
    ],
    image: alwaysPhoto,
  },
  {
    id: "channels",
    tab: "多渠道接待",
    title: "電話、WhatsApp、電郵，都有人接待。",
    label: "我們提供",
    checks: [
      <>電話及訊息<strong>自動接待</strong>，先了解客人需要</>,
      <>以<strong>廣東話、普通話及英語</strong>處理查詢</>,
      <>收集<strong>服務項目、時間與聯絡資料</strong></>,
      <>由查詢接到<strong>預約安排</strong>，按流程繼續跟進</>,
    ],
    pains: [
      "訊息散落不同 App，容易看漏",
      "客人需要重複講述需求",
      "語言不通，溝通困難",
      "對話記錄難以翻查",
    ],
    image: channelsPhoto,
  },
  {
    id: "systems",
    tab: "系統兼容",
    title: "接上你正在用的系統，無需推倒重來。",
    label: "我們提供",
    checks: [
      <><strong>兼容現有 PMS、CRM</strong> 及業務流程</>,
      <><strong>自適應你的業務需求</strong>，接待規則由你而定</>,
      <>原生支援 <strong>Google Calendar</strong>，預約自動同步</>,
      <>預約資料<strong>自動寫入系統</strong>，告別手寫重複輸入</>,
    ],
    pains: [
      "想換系統又怕推倒重來",
      "預約資料重複輸入",
      "日曆與接待脫節，容易撞期",
      "手寫登記預約，費時易錯",
    ],
    image: systemsPhoto,
  },
  {
    id: "booking",
    tab: "預約處理",
    title: "預約、改期，對話中就能辦妥。",
    label: "我們提供",
    checks: [
      <>了解<strong>服務項目、時間與聯絡資料</strong></>,
      <>查閱<strong>可用時段</strong>，按客人需要安排預約</>,
      <>按流程處理<strong>改期或取消</strong>，更新預約記錄</>,
      <>完成後<strong>回覆客人確認結果</strong>，減少來回溝通</>,
    ],
    pains: [
      "問完有冇位，仍要人手安排",
      "客人想改期，要來回確認時間",
      "舊預約未更新，容易撞期",
      "安排好咗，卻忘記回覆客人",
    ],
    image: bookingPhoto,
  },
];

const STATS = [
  { value: "80%", label: "漏接來電減少" },
  { value: "25%", label: "預約成功增加" },
  { value: "10+", unit: "小時", label: "每週為前台節省" },
  { value: "100%", label: "非辦公時段覆蓋" },
];

export function HomeValueShowcase() {
  const AUTOPLAY_MS = 6000;
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [inView, setInView] = useState(false);
  const [pageVisible, setPageVisible] = useState(true);
  const [resetKey, setResetKey] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const [reducedMotion] = useState(
    () => typeof window !== "undefined"
      && typeof window.matchMedia === "function"
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );

  /* 手動選擇後重新計時：輪播不停，只是從這一刻再數 AUTOPLAY_MS。 */
  const select = (index: number) => {
    setActive(index);
    setResetKey((key) => key + 1);
  };

  useEffect(() => {
    const node = rootRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.3 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onVisibilityChange = () => setPageVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  useEffect(() => {
    if (paused || !inView || !pageVisible || reducedMotion) return;
    const timer = window.setTimeout(() => setActive((index) => (index + 1) % PANELS.length), AUTOPLAY_MS);
    return () => window.clearTimeout(timer);
    // resetKey 讓手動點擊重新武裝計時器；active 令每次推進後接著數下一拍。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, paused, inView, pageVisible, reducedMotion, resetKey]);

  function handleTabKey(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    let next: number | undefined;
    if (event.key === "ArrowRight") next = (index + 1) % PANELS.length;
    if (event.key === "ArrowLeft") next = (index + PANELS.length - 1) % PANELS.length;
    if (next !== undefined) {
      event.preventDefault();
      select(next);
      (event.currentTarget.parentElement?.children[next] as HTMLButtonElement | undefined)?.focus();
    }
  }

  return (
    <div className="ce-hk-showcase" ref={rootRef}
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setPaused(false); }}
    >
      <div className="ce-hk-tabs" role="tablist" aria-label="ClawEase 效益">
        <div className="ce-hk-tabs-list">
          {PANELS.map((panel, index) => (
            <button key={panel.id} type="button" role="tab" aria-selected={active === index}
              className={active === index ? "ce-hk-tab is-active" : "ce-hk-tab"}
              onClick={() => select(index)} onKeyDown={(event) => handleTabKey(event, index)}
            >{panel.tab}</button>
          ))}
        </div>
      </div>
      <div className="ce-hk-track-viewport">
        <div className="ce-hk-track" style={{ "--i": active } as CSSProperties}>
          {PANELS.map((panel, index) => (
            <article key={panel.id} className={active === index ? "ce-hk-panel is-active" : "ce-hk-panel"}
              onClick={() => select(index)} aria-hidden={active !== index}>
              <div className="ce-hk-panel-text">
                <h3>{panel.title}</h3>
                <p className="ce-hk-panel-label">{panel.label}</p>
                <ul className="ce-hk-checks">
                  {panel.checks.map((check, i) => (
                    <li key={i}><span className="ce-hk-check"><Check size={11} strokeWidth={3.2} /></span><span>{check}</span></li>
                  ))}
                </ul>
                {panel.id === "always" && (
                  <div className="ce-hk-ministats">
                    {STATS.map((stat) => <div key={stat.label}><strong>{stat.value}{stat.unit ? <small>{stat.unit}</small> : null}</strong><span>{stat.label}</span></div>)}
                  </div>
                )}
              </div>
              <div className="ce-hk-visual">
                <img className="ce-hk-photo" src={panel.image} alt="" width={1122} height={1402}
                  loading="lazy" decoding="async" draggable={false} />
                <div className="ce-hk-pains">
                  <p>如果你正在經歷</p>
                  {panel.pains.map((pain, i) => (
                    <span key={pain} className="ce-hk-pain" style={{ "--n": i } as CSSProperties}>
                      <span className="ce-hk-pain-x"><X size={10} strokeWidth={3.4} /></span>
                      {pain}
                    </span>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
